import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowLeft, FiTrash2, FiCheck, FiLoader } from 'react-icons/fi'

export default function WebsiteReviews() {
  const { websiteId } = useParams()
  const navigate = useNavigate()

  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [updatingReviewId, setUpdatingReviewId] = useState(null)
  const [deletingReviewId, setDeletingReviewId] = useState(null)

  useEffect(() => {
    fetchReviews()
  }, [websiteId])

  const fetchReviews = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('userAdminToken') || localStorage.getItem('token') || localStorage.getItem('globalAdminToken')
      if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const res = await axios.get(`/reviews/website/${websiteId}`)
      setReviews(res.data.data || [])
    } catch (err) {
      console.error('Error fetching reviews:', err)
      toast.error(err.response?.data?.message || 'Failed to load reviews')
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id, status) => {
    setUpdatingReviewId(id)
    try {
      await axios.patch(`/reviews/${id}/status`, { status })
      toast.success('Status updated')
      setReviews(prev => prev.map(r => r._id === id ? { ...r, status } : r))
      if (selected?._id === id) setSelected(prev => ({ ...prev, status }))
    } catch (err) {
      console.error('Error updating status:', err)
      toast.error('Failed to update status')
    } finally {
      setUpdatingReviewId(null)
    }
  }

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return
    setDeletingReviewId(id)
    try {
      await axios.delete(`/reviews/${id}`)
      toast.success('Review deleted')
      setReviews(prev => prev.filter(r => r._id !== id))
      if (selected?._id === id) setSelected(null)
    } catch (err) {
      console.error('Error deleting review:', err)
      toast.error('Failed to delete review')
    } finally {
      setDeletingReviewId(null)
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><FiLoader className="w-12 h-12 animate-spin text-primary-600" /></div>

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/user-admin/websites')} className="p-2 hover:bg-gray-100 rounded" title="Back to Website Builder Dashboard"> <FiArrowLeft /> </button>
          <h1 className="text-2xl font-bold">Customer Reviews</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4 h-fit">
          <h2 className="font-semibold mb-4 text-base sm:text-lg">Reviews</h2>
          {reviews.length === 0 ? (
            <div className="text-gray-500 text-sm">No reviews</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {reviews.map(r => (
                <button key={r._id} onClick={() => setSelected(r)} className={`w-full text-left p-3 rounded transition-colors text-sm ${selected?._id === r._id ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50'}`}>
                  <div className="font-semibold truncate">{r.visitorName}</div>
                  <div className="text-xs text-gray-500 truncate">{r.comment}</div>
                  <div className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString()}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow p-4 sm:p-6">
          {!selected ? (
            <div className="text-gray-500 text-center py-8">Select a review to view details</div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">{selected.visitorName}</h3>
                  <div className="text-xs text-gray-500 mt-1">{new Date(selected.createdAt).toLocaleString()}</div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
                  <button 
                    onClick={() => updateStatus(selected._id, 'approved')} 
                    disabled={updatingReviewId === selected._id}
                    className="flex-1 sm:flex-none py-2 px-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded transition-colors flex items-center justify-center gap-2"
                  >
                    {updatingReviewId === selected._id && <FiLoader className="w-4 h-4 animate-spin" />}
                    {updatingReviewId === selected._id ? 'Approving...' : 'Approve'}
                  </button>
                  <button 
                    onClick={() => updateStatus(selected._id, 'rejected')} 
                    disabled={updatingReviewId === selected._id}
                    className="flex-1 sm:flex-none py-2 px-3 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded transition-colors flex items-center justify-center gap-2"
                  >
                    {updatingReviewId === selected._id && <FiLoader className="w-4 h-4 animate-spin" />}
                    {updatingReviewId === selected._id ? 'Rejecting...' : 'Reject'}
                  </button>
                  <button 
                    onClick={() => deleteReview(selected._id)} 
                    disabled={deletingReviewId === selected._id}
                    className="py-2 px-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded transition-colors flex items-center justify-center gap-2"
                  >
                    {deletingReviewId === selected._id ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-sm sm:text-base mb-2">Comment</h4>
                <p className="text-gray-700 text-sm whitespace-pre-wrap break-words">{selected.comment}</p>
              </div>

              {selected.rating && (
                <div>
                  <h4 className="font-semibold text-sm sm:text-base mb-2">Rating</h4>
                  <div className="text-yellow-600 text-lg">{'★'.repeat(selected.rating)}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
